/**
 * Industries We Serve page content.
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Industries We Serve Page (SEO Optimized 2026)" requirement document
 * (MBA_MERN_Solutions/Industries We Serve.docx). Do not reword, shorten or
 * extend this copy — update the document first.
 *
 * NOTE `servicesLabel` is per industry on purpose: the document introduces each
 * service list with different wording ("Our services include:", "MBA Solutions
 * provides support for:", "MBA Solutions supports:" ...). Those are not
 * interchangeable and must not be collapsed into one shared label.
 *
 * `icon` keys point at the Home page's existing registry (../Home/homeIcons);
 * no new artwork was introduced for this page.
 */

export const industriesSeo = {
  title:
    "Industries We Serve | ISO Certification & Compliance Solutions | MBA Solutions",
  description:
    "MBA Solutions provides ISO certification, compliance, food safety, sustainability and management system consultancy services for multiple industries including food, textile, manufacturing, IT and export businesses.",
  slug: "/industries-we-serve",
};

export const heroContent = {
  h1: "Industries We Serve",
  subtitle:
    "Supporting Businesses Across Multiple Industries With Global Standards",
  paragraphs: [
    "Every industry has unique challenges, regulations and customer expectations. MBA Solutions provides industry-focused certification and compliance solutions designed to help organizations improve processes, achieve international standards and grow with confidence.",
    "Our consultants understand different industry requirements and provide practical solutions that align with operational goals, market demands and global compliance expectations.",
  ],
};

export const helpLabel = "How We Help:";

export const industries = [
  {
    id: "food-beverage",
    icon: "food",
    title: "Food & Beverage Industry",
    subtitle: "Building Safer and More Reliable Food Businesses",
    description:
      "MBA Solutions supports food manufacturers, processors, exporters and related businesses in implementing internationally recognized food safety systems.",
    servicesLabel: "Our services include:",
    services: [
      "HACCP Certification",
      "ISO 22000 Food Safety Management System",
      "FSSC 22000",
      "BRCGS Food Safety",
      "Halal Certification",
      "GMP & GHP Implementation",
    ],
    help: [
      "Improve food safety controls",
      "Reduce operational risks",
      "Meet buyer requirements",
      "Strengthen customer trust",
      "Access international markets",
    ],
  },
  {
    id: "textile-garments",
    icon: "textile",
    title: "Textile & Garments Industry",
    subtitle: "Supporting Sustainable & Responsible Textile Manufacturing",
    description:
      "The textile industry faces increasing demand for sustainability, ethical production and responsible sourcing. MBA Solutions helps textile manufacturers and exporters meet international compliance requirements.",
    servicesLabel: "Our services include:",
    services: [
      "GOTS Certification",
      "GRS Certification",
      "RCS & OCS",
      "WRAP Certification",
      "Sedex / SMETA",
      "Sustainability Assessments",
    ],
    help: [
      "Meet international buyer expectations",
      "Improve sustainability performance",
      "Strengthen supply chain transparency",
      "Support responsible manufacturing practices",
    ],
  },
  {
    id: "manufacturing",
    icon: "manufacturing",
    title: "Manufacturing Industry",
    subtitle: "Improving Quality, Safety & Operational Excellence",
    description:
      "Manufacturing organizations require strong systems to improve efficiency, reduce risks and maintain consistent product quality.",
    servicesLabel: "MBA Solutions provides support for:",
    services: [
      "ISO 9001 Quality Management System",
      "ISO 14001 Environmental Management",
      "ISO 45001 Occupational Health & Safety",
      "ISO 50001 Energy Management",
      "Process Improvement Systems",
    ],
    help: [
      "Improve operational efficiency",
      "Reduce production risks",
      "Standardize processes",
      "Build continuous improvement culture",
    ],
  },
  {
    id: "it-software",
    icon: "it",
    title: "IT & Software Industry",
    subtitle: "Protecting Information and Improving Business Systems",
    description:
      "Technology companies require strong information security and quality management practices to maintain customer confidence.",
    servicesLabel: "MBA Solutions supports IT organizations with:",
    services: [
      "ISO 27001 Information Security",
      "ISO 9001 Quality Management",
      "Business Process Improvement",
      "Compliance Consulting",
    ],
    help: [
      "Protect sensitive information",
      "Improve internal processes",
      "Build customer trust",
      "Meet international requirements",
    ],
  },
  {
    id: "engineering",
    icon: "engineering",
    title: "Engineering Industry",
    subtitle: "Strengthening Technical Excellence & Compliance",
    description:
      "Engineering companies require reliable management systems to improve quality, safety and operational control.",
    servicesLabel: "Our solutions include:",
    services: [
      "ISO Management Systems",
      "Quality Assurance Support",
      "Process Documentation",
      "Internal Audit Support",
    ],
    help: [
      "Improve project management",
      "Reduce technical risks",
      "Enhance quality performance",
      "Meet industry standards",
    ],
  },
  {
    id: "hospitality-tourism",
    icon: "hospitality",
    title: "Hospitality & Tourism Industry",
    subtitle: "Creating Better Service Standards",
    description:
      "Hospitality businesses need strong systems to improve service quality, safety and customer experience.",
    servicesLabel: "MBA Solutions supports:",
    services: [
      "Quality Management Systems",
      "Food Safety Standards",
      "Environmental Practices",
      "Sustainability Solutions",
    ],
    help: [
      "Improve service consistency",
      "Enhance customer satisfaction",
      "Support sustainable operations",
    ],
  },
  {
    id: "export-businesses",
    icon: "export",
    title: "Export Businesses",
    subtitle: "Helping Businesses Meet Global Market Requirements",
    description:
      "Exporters must comply with international standards to build buyer confidence and access new markets.",
    servicesLabel: "MBA Solutions helps exporters with:",
    services: [
      "ISO Certifications",
      "Buyer Compliance Requirements",
      "Sustainability Standards",
      "Supply Chain Certifications",
    ],
    help: [
      "Improve export readiness",
      "Meet international expectations",
      "Strengthen buyer relationships",
    ],
  },
  {
    id: "supply-chain-logistics",
    icon: "supplyChain",
    title: "Supply Chain & Logistics",
    subtitle: "Building Transparent and Responsible Supply Chains",
    description:
      "Modern supply chains require visibility, compliance and responsible sourcing practices.",
    servicesLabel: "MBA Solutions provides:",
    services: [
      "FSC Chain of Custody",
      "Sustainability Assessments",
      "Supplier Compliance Support",
      "ESG Solutions",
    ],
    help: [
      "Improve supply chain transparency",
      "Support responsible sourcing",
      "Meet global compliance needs",
    ],
  },
];

export const approachContent = {
  title: "Our Industry Approach",
  subtitle: "Practical Solutions Designed Around Your Business",
  intro:
    "At MBA Solutions, we understand that every organization is different. Our consultants analyze your current systems, identify improvement opportunities and develop solutions that match your industry requirements.",
  focusLabel: "Our approach focuses on:",
  focus: [
    "Business Understanding",
    "Gap Assessment",
    "Practical Implementation",
    "Certification Readiness",
    "Continuous Improvement",
  ],
};

export const finalCtaContent = {
  title: "Ready to Improve Your Industry Compliance?",
  paragraph:
    "Whether you are a manufacturer, exporter, service provider or growing business, MBA Solutions can help you achieve international standards and build stronger systems.",
  cta: { label: "Contact MBA Solutions Today", to: "/contact" },
};
